import { Params } from '@angular/router';

import { params } from '@app/configurations';
import { Account, AdPlatform, AdPlatformType, Layout } from '@app/core/models/common.models';
import {
  BUDGET_AMOUNT,
  CONVERSIONS,
  COST_DAY,
  COST_REVENUE_RATIO,
  COST_REVENUE_TOTAL_RATIO,
  COST_REVENUE_TOTAL_RATIO_ROLLING_AVG,
  CRITERION_MAX_CPC,
  RETURN_ON_INVESTMENT,
  REVENUE,
  REVENUE_ORGANIC,
  REVENUE_ORGANIC_ROLLING_AVG,
  REVENUE_PER_CONVERSION,
  REVENUE_TOTAL,
  TacosMetrics,
  VCPMMetricMaps,
} from '@app/core/models/metrics.constants';
import { GlobalFilterDefaults } from '@app/core/store/states/global-filters/global-filters-state.models';
import { assertArray, intersection } from '@app/core/utilities/array.utilities';
import {
  GetDateFilters,
  getFilterAndSearchRefinersFromCurrentState,
  transformDelayObject,
} from '@app/core/utilities/refiners.utilities';
import { Capitalize, SplitHyphensAndCapitalize } from '@app/core/utilities/string.utilities';
import { TableLayoutColumn } from '@app/shared/modules/table-layout/models/table-layout.model';
import {
  accountRefinerConfig,
  adGroupRefinersConfig,
  campaignAccountRefinersConfig,
  campaignRefinersConfig,
  criteriaRefinersConfig,
  KeyFiguresRefinersConfig,
  pgRefinersConfig,
} from '../models/key-figures-refiners.models';
import { KeyFiguresColumn, keyFiguresCustomColumns, KeyFiguresTab } from '../models/key-figures.models';
import { canCreateCampaign } from '@app/campaign-creation/shared/campaign-creation.utilities';

export function shouldUseAccountEndpointForChart(tab: KeyFiguresTab, state: Params): boolean {
  return !!(
    tab === KeyFiguresTab.Campaign &&
    (state[params.GLOBAL_FILTER_CAMPAIGN_ID] || state[params.GLOBAL_FILTER_CRITERIA_ID])
  );
}

export function getRefinersFromState(
  tab: KeyFiguresTab,
  state: Params,
  defaultFilters: GlobalFilterDefaults,
  forAccountEndpoint = false,
): [string, string | number | boolean | null][] | false {
  const tabRefiners = getFieldsFromTab(tab, state, defaultFilters, forAccountEndpoint);

  if (!tabRefiners) return false;

  return [...GetDateFilters(state[params.GLOBAL_FILTER_DATE_RANGE]), ...tabRefiners, ['currency', state.currency]];
}

export function getFieldsFromTab(
  tab: KeyFiguresTab,
  state: Params,
  defaultFilters: GlobalFilterDefaults,
  forAccountEndpoint = false,
): [string, string][] | false {
  const config = {
    [KeyFiguresTab.Account]: accountRefinerConfig,
    [KeyFiguresTab.PG]: pgRefinersConfig,
    [KeyFiguresTab.Campaign]: forAccountEndpoint ? campaignAccountRefinersConfig : campaignRefinersConfig,
    [KeyFiguresTab.Adgroup]: adGroupRefinersConfig,
    [KeyFiguresTab.Criteria]: criteriaRefinersConfig,
  }[tab];

  return getURLParams(state, defaultFilters, config);
}

function getURLParams(
  state: Params,
  defaultFilters: GlobalFilterDefaults,
  config: KeyFiguresRefinersConfig,
): [string, string][] | false {
  const urlFilterValues = getFilterAndSearchRefinersFromCurrentState(state, {}, defaultFilters, {
    includeAdGroup: true,
    includeCriteria: true,
    forApiMutator: false,
    xAccount: true,
  });

  if (config.invalid?.some((invalid) => urlFilterValues.some(([filter]) => filter.startsWith(invalid)))) {
    return false;
  }

  const values: [string, string][] = [
    ['group_by', config.groupBy],
    ...urlFilterValues.filter(([filter]) => config.filters.some((configFilter) => filter.startsWith(configFilter))),
  ];

  if (state[params.GLOBAL_FILTER_SEARCH_QUERY]) {
    values.push(['search_term', encodeURIComponent(state[params.GLOBAL_FILTER_SEARCH_QUERY])]);
    values.push(['search_fields', config.search.join(',')]);
  }

  values.forEach((d) => {
    // updated PG filter param names
    if (d[0] === 'filter_optimization_type') d[0] = 'filter_performance_group_optimization_type';

    if (d[0] === 'filter_goal_types') d[0] = 'filter_performance_group_goal_types';
  });

  return values;
}

export function getAdspertId(state: Params, defaultFilters: GlobalFilterDefaults): string {
  const refiners = getFilterAndSearchRefinersFromCurrentState(state, {}, defaultFilters, {
    includeAdGroup: true,
    includeCriteria: true,
    forApiMutator: false,
    xAccount: true,
  });

  const adspertId = (state[params.GLOBAL_FILTER_CAMPAIGN_ID] || state[params.GLOBAL_FILTER_AD_GROUPS_ID])?.split(
    ':',
  )[0];

  return refiners.find(([key]) => key === 'filter_adspert_account_id')?.[1] || adspertId;
}

export function hasAdGroupsOrCriteria(state: Params): boolean {
  return (
    intersection(Object.keys(state), [
      params.GLOBAL_FILTER_AD_GROUPS_ID,
      params.GLOBAL_FILTER_CRITERIA_ID,
      params.GLOBAL_FILTER_AD_GROUPS_QUERY,
      params.GLOBAL_FILTER_CRITERIA_QUERY,
    ]).length > 0
  );
}

export function isAdGroupsTabDisabled(state: Params): boolean {
  return (
    !state[params.GLOBAL_FILTER_AD_GROUPS_ID] && !state[params.ADSPERT_ID] && !state[params.GLOBAL_FILTER_CAMPAIGN_ID]
  );
}

export function isCriteriaTabDisabled(state: Params): boolean {
  return !state[params.ADSPERT_ID] && !state[params.GLOBAL_FILTER_AD_GROUPS_ID];
}

export function getTabFromParams(state: Params): KeyFiguresTab {
  return parseInt(state[params.KEY_FIGURE_TAB]) || KeyFiguresTab.Account;
}

export function isTacosMetric(id: string): boolean {
  return TacosMetrics.includes(id);
}

export function getVisibleColumns(
  customColumns: TableLayoutColumn[],
  hasProfit: boolean,
  platforms: AdPlatform[],
  translations: object,
  layout: Layout,
  tab: KeyFiguresTab,
  items: any[],
  lessThan7: boolean,
  hasFilter: boolean,
): KeyFiguresColumn[] {
  const headers: Record<string, { title: string; tooltip: string }> = Object.values(translations as object).reduce(
    (a, b) => ({ ...a, ...b }),
    {},
  );

  const hasCampaignEditSupport = platforms.some((platform) => canCreateCampaign({ platform }));

  const hasSelectCol =
    hasCampaignEditSupport && [KeyFiguresTab.Campaign, KeyFiguresTab.Adgroup, KeyFiguresTab.Criteria].includes(tab);
  const hasActionCol =
    hasCampaignEditSupport && [KeyFiguresTab.Campaign, KeyFiguresTab.Adgroup, KeyFiguresTab.Criteria].includes(tab);
  const hasBudgetCol = [KeyFiguresTab.Campaign].includes(tab);
  const hasTypeCol = [KeyFiguresTab.Campaign, KeyFiguresTab.Criteria].includes(tab);
  const hasStatusCol = ![KeyFiguresTab.Account, KeyFiguresTab.PG].includes(tab);
  const hasOptimizeCol = [KeyFiguresTab.Account, KeyFiguresTab.Campaign].includes(tab);
  const hasGoalsCol = [KeyFiguresTab.PG].includes(tab);
  const hasMaxCpcCol = [KeyFiguresTab.Criteria].includes(tab);

  const columns: KeyFiguresColumn[] = [
    ...(hasSelectCol
      ? [
          {
            id: 'select',
            left: true,
            width: 50,
            visible: true,
            align: 'center' as const,
          },
        ]
      : []),
    ...(hasActionCol
      ? [
          {
            id: 'action',
            left: true,
            width: 70,
            title: headers.action.title,
            tooltip: headers.action.tooltip,
            visible: true,
            align: 'center' as const,
          },
        ]
      : []),
    {
      id: 'title',
      left: true,
      width: layout === 'MOBILE' ? 250 : 450,
      sortable: true,
      title: headers.name.title,
      tooltip: headers.name.tooltip,
      visible: true,
    },
  ];

  const ids: string[] = [
    ...(hasStatusCol ? ['status'] : []),
    ...(hasOptimizeCol ? ['optimize'] : []),
    ...(hasBudgetCol ? [BUDGET_AMOUNT] : []),
    ...(hasGoalsCol ? ['goals'] : []),
    ...(hasTypeCol ? ['type'] : []),
    ...(hasMaxCpcCol ? [CRITERION_MAX_CPC] : []),
  ];

  const sortableColumns = ['goals'];

  ids.forEach((id) => {
    const columnHeader = id === 'type' && KeyFiguresTab.Criteria == tab ? headers['criteria-type'] : headers[id];

    return columns.push({
      id,
      width: ['status', 'optimize'].includes(id) ? 70 : 110,
      title: columnHeader?.title,
      tooltip: columnHeader?.tooltip,
      align: [CRITERION_MAX_CPC, BUDGET_AMOUNT].includes(id)
        ? 'right'
        : ['status', 'optimize'].includes(id)
          ? 'center'
          : 'left',
      isCustom: id === BUDGET_AMOUNT,
      visible: true,
      sortable: sortableColumns.includes(id),
    });
  });

  const hasProfitOptimization = [KeyFiguresTab.Account, KeyFiguresTab.PG].includes(tab)
    ? hasProfit
    : items.some((item) => item.campaignOptimizationType === 'PROFIT');

  const isNoProfitOptimization = [KeyFiguresTab.Account, KeyFiguresTab.PG].includes(tab)
    ? !hasProfit
    : items.every((item) => item.campaignOptimizationType !== 'PROFIT');

  const isOnlyAmazon =
    platforms.every((platform) => platform === 'AMAZON') ||
    items.every((item) => assertArray(item.platform).every((platform) => platform === 'AMAZON'));

  const hasAmazon = platforms.includes('AMAZON');

  columns.push(
    ...customColumns
      .filter(({ id }) => {
        if (id.startsWith('profit') || id === RETURN_ON_INVESTMENT) {
          return !isNoProfitOptimization;
        }

        if (isTacosMetric(id)) {
          return hasAmazon && tab === KeyFiguresTab.Account;
        }

        return true;
      })
      .map((customColumn) => {
        let translationKey = customColumn.id;
        let titleKey = 'title';

        if (lessThan7 && customColumn.id === COST_REVENUE_TOTAL_RATIO) {
          translationKey = `${customColumn.id}_rolling_avg`;
          titleKey = 'column';
        }

        let hideTooltip = [REVENUE, COST_REVENUE_RATIO].includes(customColumn.id);
        let summaryTooltipKey = `${customColumn.id}-tooltip`;

        if (isOnlyAmazon && hasProfitOptimization && [REVENUE].includes(customColumn.id)) {
          hideTooltip = false;
          summaryTooltipKey = `profit_${summaryTooltipKey}`;
        }

        if (hasFilter && customColumn.id === COST_REVENUE_TOTAL_RATIO) {
          summaryTooltipKey += '-empty';
        }
        return <KeyFiguresColumn>{
          ...keyFiguresCustomColumns[customColumn.id],
          ...customColumn,
          sortable: customColumn.id !== COST_DAY,
          title: headers[translationKey]?.[titleKey]?.replace(/\//g, ' / '),
          tooltip: headers[translationKey]?.tooltip,
          summaryTooltip: hideTooltip ? null : headers[summaryTooltipKey]?.toString(),
          headerInfoTip: !!VCPMMetricMaps[customColumn.id] ? headers[VCPMMetricMaps[customColumn.id]]?.tooltip : null,
          isCustom: true,
          align: 'right',
        };
      }),
  );

  return toggleColumnsFromData(columns);
}

export function toggleColumnsFromData(columns: KeyFiguresColumn[]): KeyFiguresColumn[] {
  const colIds = columns.map((col) => col.id);
  const hasGrossNetProfit = colIds.includes('profit_gross') || colIds.includes('profit_net');
  return hasGrossNetProfit ? columns.filter((col) => col.id !== 'profit') : columns;
}

export function transformTableData(
  data: any[],
  state: Params,
  {
    delay,
    lessThan7,
    tab,
    accounts,
    goalTranslations,
    points,
    startDate,
    customersHasNewGoalsPage,
  }: {
    delay: boolean;
    lessThan7: boolean;
    tab: KeyFiguresTab;
    accounts: Account[];
    goalTranslations: Record<string, string>;
    points: object[];
    startDate: string;
    customersHasNewGoalsPage: boolean;
  },
) {
  return data.map((item) => {
    if (item.has_vcpm) {
      //see https://adspert.atlassian.net/browse/AD-13475
      //for vcpm campaign ignore delay
      delay = false;
    }
    item = transformDelayObject({ ...item }, delay);

    if (item[REVENUE] && item[CONVERSIONS]) {
      item[REVENUE_PER_CONVERSION] = item[REVENUE] / item[CONVERSIONS];
    }

    const id = (item.adspertId || state[params.ADSPERT_ID] || item.adspertAccountId)?.toString();
    const account = accounts.find(({ adspertAccountId }) => adspertAccountId.toString() === id);

    if (!item.accountPlatform) {
      item.accountPlatform = id && account?.platform;
    }

    if (!item.platform.includes(item.accountPlatform)) {
      item.platform.push(item.accountPlatform);
    }

    if (item.platform.includes('AMAZON')) {
      if (lessThan7) {
        item[COST_REVENUE_TOTAL_RATIO] = points.length
          ? points[points.length - 1][COST_REVENUE_TOTAL_RATIO_ROLLING_AVG]
          : item[COST_REVENUE_TOTAL_RATIO_ROLLING_AVG];
      }

      if (!item[REVENUE_TOTAL] && item[REVENUE]) {
        item[REVENUE_TOTAL] = null;
      }
    } else {
      item[REVENUE_TOTAL] = null;
      item[REVENUE_ORGANIC] = null;
      item[REVENUE_ORGANIC_ROLLING_AVG] = null;
      item[COST_REVENUE_TOTAL_RATIO] = null;
      item[COST_REVENUE_TOTAL_RATIO_ROLLING_AVG] = null;
    }

    item.title = {
      [KeyFiguresTab.Account]: `${item.accountName} (${item.accountId})`,
      [KeyFiguresTab.PG]: item.performanceGroupName,
      [KeyFiguresTab.Campaign]: item.campaignName,
      [KeyFiguresTab.Adgroup]: item.adgroupName,
      [KeyFiguresTab.Criteria]: item.criterionName,
    }[tab];

    item.status = {
      [KeyFiguresTab.Campaign]: item.campaignStatus,
      [KeyFiguresTab.Adgroup]: item.adgroupStatus,
      [KeyFiguresTab.Criteria]: item.criterionStatus,
    }[tab];

    item.optimize = {
      [KeyFiguresTab.Account]: item.accountOptimize,
      [KeyFiguresTab.Campaign]: item.campaignOptimize,
    }[tab];

    item.goals = item.goals?.map((goal) => {
      const nameKey = goal.key === 'COST-PER-DAY' && customersHasNewGoalsPage ? 'maximize_sales_long' : goal.key;

      return {
        ...goal,
        name: goalTranslations[nameKey],
      };
    });

    item.campaignOptimizationType = Capitalize(item.campaignOptimizationType);
    item.campaignType = SplitHyphensAndCapitalize(item.campaignType);
    item.criterionType = SplitHyphensAndCapitalize(item.criterionType);
    item.enableVcpm = item.has_vcpm;

    item.canShowInsights = canShowInsights(tab, item);
    item.missingInsightsDate =
      item.canShowInsights && new Date(account.connectedAt).getTime() > new Date(startDate).getTime()
        ? new Date(account.connectedAt)
        : null;

    return item;
  });
}

export function getKeyFigureParams(item: any, state: Params, tab: KeyFiguresTab): Params {
  const keyFigureParams = {
    [params.CUSTOMER_ID]: state[params.CUSTOMER_ID],
    [params.GLOBAL_FILTER_DATE_RANGE]: state[params.GLOBAL_FILTER_DATE_RANGE],
    [params.ADSPERT_ID]: item.adspertId,
    [params.GLOBAL_FILTER_ACCOUNT_QUERY]: null,
    tab: Math.max(KeyFiguresTab.Account, Math.min(tab + 1, KeyFiguresTab.Criteria)),
  };

  if (tab > KeyFiguresTab.Account) {
    keyFigureParams[params.ADSPERT_ID] = state[params.ADSPERT_ID];
    keyFigureParams[params.GLOBAL_FILTER_ACCOUNT_QUERY] = state[params.GLOBAL_FILTER_ACCOUNT_QUERY];
    keyFigureParams[params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID] = item.performanceGroupId;
  }

  if (tab > KeyFiguresTab.PG) {
    keyFigureParams[params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID] = state[params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID];
    keyFigureParams[params.GLOBAL_FILTER_CAMPAIGN_ID] = item.campaignId;
    keyFigureParams[params.GLOBAL_FILTER_CAMPAIGN_QUERY] = null;
  }

  if (tab > KeyFiguresTab.Campaign) {
    keyFigureParams[params.GLOBAL_FILTER_CAMPAIGN_ID] = state[params.GLOBAL_FILTER_CAMPAIGN_ID] || item.campaignId;
    keyFigureParams[params.GLOBAL_FILTER_CAMPAIGN_QUERY] = state[params.GLOBAL_FILTER_CAMPAIGN_QUERY];
    keyFigureParams[params.GLOBAL_FILTER_AD_GROUPS_ID] = item.adspertAdgroupId;
    keyFigureParams[params.GLOBAL_FILTER_AD_GROUPS_QUERY] = null;
  }

  if (tab > KeyFiguresTab.Adgroup) {
    keyFigureParams[params.GLOBAL_FILTER_AD_GROUPS_ID] = item.adspertAdgroupId;
    keyFigureParams[params.GLOBAL_FILTER_AD_GROUPS_QUERY] = null;
    keyFigureParams[params.GLOBAL_FILTER_CRITERIA_ID] = item.adspertCriterionId;
    keyFigureParams[params.GLOBAL_FILTER_CRITERIA_QUERY] = null;
  }

  return keyFigureParams;
}

export function getAdGroupCriterionParams(item: any, state: Params, parent: KeyFiguresTab): Params {
  const routerParams = {
    [params.CUSTOMER_ID]: state[params.CUSTOMER_ID],
    [params.GLOBAL_FILTER_DATE_RANGE]: state[params.GLOBAL_FILTER_DATE_RANGE],
    [params.ADSPERT_ID]: state[params.ADSPERT_ID],
    [params.GLOBAL_FILTER_ACCOUNT_QUERY]: state[params.GLOBAL_FILTER_ACCOUNT_QUERY],
    [params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID]: state[params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID],
    [params.GLOBAL_FILTER_CAMPAIGN_ID]: item.campaignId,
    [params.GLOBAL_FILTER_CAMPAIGN_QUERY]: null,
    [params.GLOBAL_FILTER_AD_GROUPS_QUERY]: null,
    [params.GLOBAL_FILTER_CRITERIA_ID]: null,
    [params.GLOBAL_FILTER_CRITERIA_QUERY]: null,
  };

  if (parent === KeyFiguresTab.Campaign) {
    routerParams.tab = KeyFiguresTab.Campaign;
  }

  if (parent === KeyFiguresTab.Adgroup) {
    routerParams[params.GLOBAL_FILTER_AD_GROUPS_ID] = item.adspertAdgroupId;
    routerParams.tab = KeyFiguresTab.Adgroup;
  }

  return routerParams;
}

export function clearSearchFilters(queryParams: Params, tab: KeyFiguresTab): Params {
  queryParams[params.GLOBAL_FILTER_SEARCH_QUERY] = null;
  queryParams[params.GLOBAL_FILTER_CRITERIA_ID] = null;
  queryParams[params.GLOBAL_FILTER_CRITERIA_QUERY] = null;

  if (tab < KeyFiguresTab.Criteria) {
    queryParams[params.GLOBAL_FILTER_AD_GROUPS_ID] = null;
    queryParams[params.GLOBAL_FILTER_AD_GROUPS_QUERY] = null;
  }

  if (tab < KeyFiguresTab.Adgroup) {
    queryParams[params.GLOBAL_FILTER_CAMPAIGN_ID] = null;
    queryParams[params.GLOBAL_FILTER_CAMPAIGN_QUERY] = null;
  }

  return queryParams;
}

export function hasSearchFilters(queryParams: Params, tab: KeyFiguresTab): boolean {
  if (
    [params.GLOBAL_FILTER_SEARCH_QUERY, params.GLOBAL_FILTER_CRITERIA_ID, params.GLOBAL_FILTER_CRITERIA_QUERY].some(
      (param) => queryParams[param],
    )
  ) {
    return true;
  }

  if (
    tab < KeyFiguresTab.Criteria &&
    [params.GLOBAL_FILTER_AD_GROUPS_ID, params.GLOBAL_FILTER_AD_GROUPS_QUERY].some((param) => queryParams[param])
  ) {
    return true;
  }

  if (
    tab < KeyFiguresTab.Adgroup &&
    [params.GLOBAL_FILTER_CAMPAIGN_ID, params.GLOBAL_FILTER_CAMPAIGN_QUERY].some((param) => queryParams[param])
  ) {
    return true;
  }

  return false;
}

export function canShowInsights(tab: KeyFiguresTab, item): boolean {
  if (![KeyFiguresTab.Campaign, KeyFiguresTab.Adgroup].includes(tab)) {
    return false;
  }

  if (![AdPlatformType.Amazon, AdPlatformType.Google, AdPlatformType.Microsoft].includes(item.accountPlatform)) {
    return false;
  }

  return !(
    item.campaignOptimizationType === Capitalize('VCPM') &&
    item.campaignType === SplitHyphensAndCapitalize('AMAZON-SPONSORED-BRANDS')
  );
}
